import { addDoc, collection, serverTimestamp, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

export interface NotificationAnalytics {
  notificationId: string;
  userId: string;
  type: string;
  action: 'sent' | 'delivered' | 'opened' | 'clicked' | 'dismissed';
  timestamp: any;
  metadata?: Record<string, any>;
}

class NotificationAnalyticsService {
  /**
   * Registrar evento de analítica
   */
  async trackEvent(
    notificationId: string,
    userId: string,
    type: string,
    action: 'sent' | 'delivered' | 'opened' | 'clicked' | 'dismissed',
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      const analyticsData: any = {
        notificationId,
        userId,
        type,
        action,
        timestamp: serverTimestamp(),
      };

      // Solo agregar metadata si tiene valor
      if (metadata && Object.keys(metadata).length > 0) {
        analyticsData.metadata = metadata;
      }

      await addDoc(collection(db, 'notificationAnalytics'), analyticsData);
    } catch (error) {
    }
  }

  /**
   * Obtener métricas generales
   */
  async getOverallMetrics(startDate?: Date, endDate?: Date): Promise<{
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    openRate: number;
    clickRate: number;
  }> {
    try {
      const start = startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const end = endDate || new Date();

      let q = query(
        collection(db, 'notificationAnalytics'),
        where('timestamp', '>=', Timestamp.fromDate(start)),
        where('timestamp', '<=', Timestamp.fromDate(end))
      );

      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => doc.data());

      const sent = events.filter(e => e.action === 'sent').length;
      const delivered = events.filter(e => e.action === 'delivered').length;
      const opened = events.filter(e => e.action === 'opened').length;
      const clicked = events.filter(e => e.action === 'clicked').length;

      const openRate = delivered > 0 ? (opened / delivered) * 100 : 0;
      const clickRate = opened > 0 ? (clicked / opened) * 100 : 0;

      return {
        sent,
        delivered,
        opened,
        clicked,
        openRate: Math.round(openRate * 10) / 10,
        clickRate: Math.round(clickRate * 10) / 10,
      };
    } catch (error) {
      return {
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        openRate: 0,
        clickRate: 0,
      };
    }
  }

  /**
   * Obtener métricas por tipo de notificación
   */
  async getMetricsByType(startDate?: Date, endDate?: Date): Promise<Record<string, any>> {
    try {
      const start = startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const end = endDate || new Date();

      let q = query(
        collection(db, 'notificationAnalytics'),
        where('timestamp', '>=', Timestamp.fromDate(start)),
        where('timestamp', '<=', Timestamp.fromDate(end))
      );

      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => doc.data());

      const typeMetrics: Record<string, any> = {};

      events.forEach(event => {
        if (!typeMetrics[event.type]) {
          typeMetrics[event.type] = {
            sent: 0,
            delivered: 0,
            opened: 0,
            clicked: 0,
          };
        }

        if (event.action === 'sent') typeMetrics[event.type].sent++;
        if (event.action === 'delivered') typeMetrics[event.type].delivered++;
        if (event.action === 'opened') typeMetrics[event.type].opened++;
        if (event.action === 'clicked') typeMetrics[event.type].clicked++;
      });

      // Calcular tasas
      Object.keys(typeMetrics).forEach(type => {
        const metrics = typeMetrics[type];
        metrics.openRate = metrics.delivered > 0 
          ? Math.round((metrics.opened / metrics.delivered) * 1000) / 10 
          : 0;
        metrics.clickRate = metrics.opened > 0 
          ? Math.round((metrics.clicked / metrics.opened) * 1000) / 10 
          : 0;
      });

      return typeMetrics;
    } catch (error) {
      return {};
    }
  }

  /**
   * Obtener usuarios más activos
   */
  async getTopUsers(limit: number = 10, startDate?: Date, endDate?: Date): Promise<Array<{
    userId: string;
    totalNotifications: number;
    opened: number;
    clicked: number;
  }>> {
    try {
      const start = startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const end = endDate || new Date();

      let q = query(
        collection(db, 'notificationAnalytics'),
        where('timestamp', '>=', Timestamp.fromDate(start)),
        where('timestamp', '<=', Timestamp.fromDate(end))
      );

      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => doc.data());

      const userStats: Record<string, any> = {};

      events.forEach(event => {
        if (!userStats[event.userId]) {
          userStats[event.userId] = {
            userId: event.userId,
            totalNotifications: 0,
            opened: 0,
            clicked: 0,
          };
        }

        if (event.action === 'delivered') userStats[event.userId].totalNotifications++;
        if (event.action === 'opened') userStats[event.userId].opened++;
        if (event.action === 'clicked') userStats[event.userId].clicked++;
      });

      return Object.values(userStats)
        .sort((a: any, b: any) => b.totalNotifications - a.totalNotifications)
        .slice(0, limit);
    } catch (error) {
      return [];
    }
  }

  /**
   * Obtener tendencias diarias
   */
  async getDailyTrends(days: number = 30): Promise<Array<{
    date: string;
    sent: number;
    opened: number;
    clicked: number;
  }>> {
    try {
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      
      let q = query(
        collection(db, 'notificationAnalytics'),
        where('timestamp', '>=', Timestamp.fromDate(startDate))
      );

      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => doc.data());

      const dailyData: Record<string, any> = {};

      events.forEach(event => {
        const date = event.timestamp?.toDate?.();
        if (!date) return;

        const dateKey = date.toISOString().split('T')[0];

        if (!dailyData[dateKey]) {
          dailyData[dateKey] = {
            date: dateKey,
            sent: 0,
            opened: 0,
            clicked: 0,
          };
        }

        if (event.action === 'sent') dailyData[dateKey].sent++;
        if (event.action === 'opened') dailyData[dateKey].opened++;
        if (event.action === 'clicked') dailyData[dateKey].clicked++;
      });

      return Object.values(dailyData).sort((a: any, b: any) => 
        a.date.localeCompare(b.date)
      );
    } catch (error) {
      return [];
    }
  }
}

const notificationAnalytics = new NotificationAnalyticsService();

export default notificationAnalytics;
